/**
 * ACADS — the HQ Finance view of the Kolkata tuition business.
 *
 * The centre page (Acads) is where fees are recorded, read teacher-wise and
 * day-wise, and corrected; HQ uses those same screens for that. This page is
 * what HQ does: the month at a glance, a read-only copy of Daily receipts,
 * the month's reconciliation, and freezing a month once it is checked. Each
 * tab's filters (month, centre, period) are drawn in the top bar.
 */
import {
  Button,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@material-ui/core'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'

import { openingMonth } from './Acads'
import { RegisterTab } from './AcadsCollect'
import { DashboardTab, TeacherBreakup, useStyles } from './AcadsEntry'
import AcadsFreeze from './AcadsFreeze'
import AcadsReco from './AcadsReco'

const TAB_KEY = 'acadsHq.tab'

const FIRST_MONTH = '2026-04' // the financial year the records start in
const MONTHS = () => {
  const months = []
  for (
    let m = dayjs().startOf('month');
    m.format('YYYY-MM') >= FIRST_MONTH;
    m = m.subtract(1, 'month')
  ) {
    months.push([m.format('YYYY-MM'), m.format('MMMM YYYY')])
  }
  return months
}
const CENTRES = [
  ['', 'Both centres'],
  ['TR', 'Theatre Road'],
  ['SL', 'Salt Lake']
]

const TABS = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'receipts', label: 'Daily receipts' },
  { id: 'reco', label: 'Reconciliation' },
  { id: 'freeze', label: 'Freeze month' }
]

const AcadsHq = () => {
  const classes = useStyles()
  const [tab, setTab] = useState(() => {
    const saved = localStorage.getItem(TAB_KEY)
    return TABS.some((t) => t.id === saved) ? saved : 'dashboard'
  })
  // One period and one centre for the whole page: switching between the
  // dashboard, the receipts and the reconciliation keeps looking at the same
  // month. The dashboard is for both centres, so it has no centre.
  const [month, setMonth] = useState(openingMonth())
  const [centre, setCentre] = useState('')
  // "April to date" — the dashboard only; it runs up to the latest month.
  // Receipts and the reco work one month at a time.
  const [ytd, setYtd] = useState(false)
  const latest = MONTHS()[0][0]

  useEffect(() => {
    localStorage.setItem(TAB_KEY, tab)
  }, [tab])

  // A figure clicked in the dashboard's teacher table opens, in place of the
  // dashboard, the student-by-student list that adds up to it.
  const [breakup, setBreakup] = useState(null)
  const openFigure = (figure, teacher) => setBreakup({ figure, teacher })

  // Where a tab draws its own filters, so month, centre and period sit in
  // the top bar on every tab.
  const [slot, setSlot] = useState(null)

  return (
    <Paper style={{ padding: 16 }} elevation={0}>
      <div className={classes.toolbar}>
        <Typography variant="h6">ACADS — HQ view</Typography>
        {tab !== 'freeze' && (
          <TextField
            select
            size="small"
            variant="outlined"
            label="Period"
            style={{ minWidth: 170 }}
            value={ytd ? 'ytd' : month}
            onChange={(e) => {
              const value = e.target.value
              setYtd(value === 'ytd')
              setBreakup(null)
              setMonth(value === 'ytd' ? latest : value)
            }}
          >
            {MONTHS().map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
            {tab === 'dashboard' && (
              <MenuItem value="ytd">April to date (cumulative)</MenuItem>
            )}
          </TextField>
        )}
        {(tab === 'receipts' || tab === 'reco') && (
          <TextField
            select
            size="small"
            variant="outlined"
            label="Centre"
            style={{ minWidth: 160 }}
            value={centre}
            onChange={(e) => setCentre(e.target.value)}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ displayEmpty: true }}
          >
            {CENTRES.map(([value, label]) => (
              <MenuItem key={label} value={value}>
                {label}
              </MenuItem>
            ))}
          </TextField>
        )}
        <div
          ref={setSlot}
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            alignItems: 'center'
          }}
        />
      </div>

      <Tabs
        value={tab}
        onChange={(e, v) => {
          setTab(v)
          setBreakup(null)
          if (v !== 'dashboard') setYtd(false)
        }}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
        scrollButtons="auto"
        style={{ marginBottom: 16 }}
      >
        {TABS.map((t) => (
          <Tab key={t.id} value={t.id} label={t.label} />
        ))}
      </Tabs>

      {tab === 'dashboard' && !breakup && (
        <DashboardTab
          month={month}
          ytd={ytd}
          onOpen={openFigure}
          classes={classes}
        />
      )}
      {tab === 'dashboard' && breakup && (
        <>
          <Button
            size="small"
            variant="outlined"
            style={{ marginBottom: 12 }}
            onClick={() => setBreakup(null)}
          >
            ‹ Back to dashboard
          </Button>
          <TeacherBreakup
            month={month}
            teacher={breakup.teacher}
            figure={breakup.figure}
            classes={classes}
          />
        </>
      )}
      {tab === 'receipts' && <RegisterTab readOnly fixed={{ month, centre }} />}
      {tab === 'reco' && <AcadsReco month={month} centre={centre} />}
      {tab === 'freeze' && <AcadsFreeze filterSlot={slot} />}
    </Paper>
  )
}

export default AcadsHq
